import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Plus, Trash2, Loader2, Mic, X } from 'lucide-react';
import LiteShell, { StepIndicator } from '../LiteShell';
import DeIdPanel from '@/components/lite/DeIdPanel';
import OutputView from '@/components/lite/OutputView';
import RateLimitGate from '@/components/lite/RateLimitGate';
import WritingStylePanel, { getSavedStyleSummary } from '@/components/lite/WritingStylePanel';
import DictationError, { IframeDictationNotice } from '@/components/lite/DictationError';
import useDictation from '@/hooks/useDictation';
import { liteApi } from '@/utils/lite/liteApi';

const AGE_RANGES = ['Under 3', '3-5', '5-8', '8-12', '12-18', 'Adult'];
const SETTINGS = ['Clinic', 'School', 'Home', 'Telehealth', 'Mixed'];
const TONE_OPTIONS = [{ value: 'concise', label: 'Concise' }, { value: 'balanced', label: 'Balanced' }, { value: 'detailed', label: 'Detailed' }];
const AUDIENCE_OPTIONS = [{ value: 'clinician', label: 'Clinician' }, { value: 'school', label: 'School / IEP team' }, { value: 'mixed', label: 'Mixed' }];
const INFORMAL_TYPES = [
  'Language sample',
  'Parent interview',
  'Play observation',
  'Dynamic assessment',
  'Classroom observation',
  'Communication sample',
  'Other',
];

const voiceSupported = () =>
  typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

export default function ReportBuilder() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    age_range: '',
    diagnosis: [],
    diagnosisInput: '',
    setting: '',
    standardized: [{ name: '', score: '', percentile: '', notes: '' }],
    informal: [{ type: '', findings: '' }],
    observed: '',
    strengths: '',
    challenges: '',
    clinical_impression: '',
    include_recommendations: true,
    include_goals: true,
    tone: 'balanced',
    audience: 'clinician',
  });
  const {
    recordingField,
    error: dictationError,
    inIframe: dictationInIframe,
    start: dictate,
    dismissError: dismissDictationError,
  } = useDictation({
    onTranscript: (fieldKey, text) => {
      setForm((f) => ({ ...f, [fieldKey]: (f[fieldKey] ? f[fieldKey] + ' ' : '') + text }));
    },
    maxSeconds: 30,
  });
  const [combinedFreeText, setCombinedFreeText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [output, setOutput] = useState('');
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState('');
  const [gateOpen, setGateOpen] = useState(false);
  const [gateInfo, setGateInfo] = useState({ used: 0, limit: 0 });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Diagnosis chips
  const addDiagnosis = () => {
    const v = form.diagnosisInput.trim();
    if (!v) return;
    if (form.diagnosis.includes(v)) return;
    update('diagnosis', [...form.diagnosis, v]);
    update('diagnosisInput', '');
  };
  const removeDiagnosis = (d) => update('diagnosis', form.diagnosis.filter((x) => x !== d));

  // Standardized assessments
  const updateStd = (i, k, v) => {
    const arr = [...form.standardized];
    arr[i] = { ...arr[i], [k]: v };
    update('standardized', arr);
  };
  const addStd = () => update('standardized', [...form.standardized, { name: '', score: '', percentile: '', notes: '' }]);
  const removeStd = (i) => update('standardized', form.standardized.filter((_, idx) => idx !== i));

  // Informal measures
  const updateInf = (i, k, v) => {
    const arr = [...form.informal];
    arr[i] = { ...arr[i], [k]: v };
    update('informal', arr);
  };
  const addInf = () => update('informal', [...form.informal, { type: '', findings: '' }]);
  const removeInf = (i) => update('informal', form.informal.filter((_, idx) => idx !== i));

  // Voice dictation handled by useDictation hook above

  const buildFreeText = () => [
    form.observed && `Observations: ${form.observed}`,
    form.strengths && `Strengths: ${form.strengths}`,
    form.challenges && `Challenges: ${form.challenges}`,
    form.clinical_impression && `Clinical impression: ${form.clinical_impression}`,
    ...form.informal.filter(m => m.findings).map(m => `${m.type || 'Informal'}: ${m.findings}`),
    ...form.standardized.filter(a => a.notes).map(a => `${a.name} notes: ${a.notes}`),
  ].filter(Boolean).join('\n\n');

  const goToDeId = () => {
    setError('');
    if (!form.age_range) {
      setError('Please select an age range.');
      return;
    }
    const hasAnyData = form.standardized.some(a => a.name) ||
                       form.informal.some(m => m.findings) ||
                       form.observed.trim() || form.strengths.trim() || form.challenges.trim();
    if (!hasAnyData) {
      setError('Please add at least one assessment, informal measure, or observation.');
      return;
    }
    setCombinedFreeText(buildFreeText());
    setStep(2);
  };

  const proceedAfterDeId = (cleaned) => {
    setStep(3);
    runGenerate(cleaned);
  };

  const runGenerate = async (cleanedFree) => {
    setGenerating(true);
    setOutput('');
    setError('');
    try {
      let email = null;
      try { email = localStorage.getItem('playfirst_lite_email'); } catch {}
      const styleSummary = getSavedStyleSummary();

      // Use original structured fields (which the user already de-identified
      // via the DeIdPanel review). The cleanedFree confirms they reviewed the
      // free text — we treat their original entries as approved at this point.
      const payload = {
        age_range: form.age_range,
        diagnosis: form.diagnosis,
        setting: form.setting,
        standardized_assessments: form.standardized.filter(a => a.name || a.score),
        informal_measures: form.informal.filter(m => m.type || m.findings),
        observed: form.observed,
        strengths: form.strengths,
        challenges: form.challenges,
        clinical_impression: form.clinical_impression,
        include_recommendations: form.include_recommendations,
        include_goals: form.include_goals,
        tone: form.tone,
        audience: form.audience,
        style_summary: styleSummary || null,
        confirm_deidentified: true,
        email,
      };
      const res = await liteApi.generateReport(payload);
      setOutput(res.output);
      setUsage({ used_today: res.used_today, daily_limit: res.daily_limit });
      setStep(4);
    } catch (e) {
      if (e.status === 429) {
        const d = e.data?.detail || {};
        setGateInfo({ used: d.used || 0, limit: d.limit || 5 });
        setGateOpen(true);
        setStep(1);
      } else {
        setError(e.message || 'Generation failed.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const VoiceBtn = ({ field, testId }) => voiceSupported() && (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => dictate(field)}
      className={`absolute right-1 top-1 h-7 rounded-full ${recordingField === field ? 'text-red-600 bg-red-50' : 'text-stone-500'}`}
      data-testid={testId}
    >
      <Mic className={`w-3.5 h-3.5 mr-1 ${recordingField === field ? 'animate-pulse' : ''}`} />
      {recordingField === field ? 'Listening…' : 'Dictate'}
    </Button>
  );

  return (
    <LiteShell currentStep={step}>
      <h1 className="text-3xl font-serif text-primary-900 mb-2">Report Builder</h1>
      <p className="text-stone-500 mb-6">
        Section-by-section clinical interpretation, in your voice, from de-identified inputs.
      </p>
      <StepIndicator step={step} />

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="lite-report-error">{error}</div>
      )}

      {step === 1 && (
        <div className="space-y-6">
          <IframeDictationNotice inIframe={dictationInIframe} />
          <DictationError error={dictationError} inIframe={dictationInIframe} onDismiss={dismissDictationError} />
          {/* SECTION 1 — Profile */}
          <Section number="1" title="Child profile">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Age range</Label>
                <Select value={form.age_range} onValueChange={(v) => update('age_range', v)}>
                  <SelectTrigger data-testid="report-age-select"><SelectValue placeholder="Select age range" /></SelectTrigger>
                  <SelectContent>{AGE_RANGES.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Setting</Label>
                <Select value={form.setting} onValueChange={(v) => update('setting', v)}>
                  <SelectTrigger data-testid="report-setting-select"><SelectValue placeholder="Select setting" /></SelectTrigger>
                  <SelectContent>{SETTINGS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Diagnosis / clinical context</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Type and press Enter (e.g. Autism, GLP)"
                  value={form.diagnosisInput}
                  onChange={(e) => update('diagnosisInput', e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDiagnosis(); } }}
                  data-testid="report-diagnosis-input"
                />
                <Button type="button" variant="outline" onClick={addDiagnosis} className="rounded-full" data-testid="report-add-dx-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {form.diagnosis.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.diagnosis.map(d => (
                    <span key={d} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm" data-testid={`report-dx-chip-${d}`}>
                      {d}
                      <button type="button" onClick={() => removeDiagnosis(d)} className="hover:text-primary-700"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </Section>

          {/* SECTION 2A — Standardized */}
          <Section number="2A" title="Standardized assessments" subtitle="Tests with formal scores. AI will give quantitative interpretation.">
            <div className="space-y-2">
              {form.standardized.map((a, i) => (
                <div key={i} className="grid sm:grid-cols-[1fr,90px,140px,1fr,40px] gap-2">
                  <Input placeholder="Test name (e.g. CELF-5)" value={a.name} onChange={(e) => updateStd(i, 'name', e.target.value)} data-testid={`std-name-${i}`} />
                  <Input placeholder="Score" value={a.score} onChange={(e) => updateStd(i, 'score', e.target.value)} data-testid={`std-score-${i}`} />
                  <Input placeholder="Percentile/range" value={a.percentile} onChange={(e) => updateStd(i, 'percentile', e.target.value)} data-testid={`std-pct-${i}`} />
                  <Input placeholder="Notes (optional)" value={a.notes} onChange={(e) => updateStd(i, 'notes', e.target.value)} data-testid={`std-notes-${i}`} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeStd(i)} disabled={form.standardized.length <= 1} data-testid={`std-remove-${i}`}>
                    <Trash2 className="w-4 h-4 text-stone-400" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addStd} className="rounded-full" data-testid="std-add-btn">
                <Plus className="w-4 h-4 mr-1" /> Add standardized test
              </Button>
            </div>
          </Section>

          {/* SECTION 2B — Informal */}
          <Section number="2B" title="Informal / observational measures" subtitle="Language samples, parent interviews, play observations. AI will give qualitative interpretation.">
            <div className="space-y-2">
              {form.informal.map((m, i) => (
                <div key={i} className="grid sm:grid-cols-[180px,1fr,40px] gap-2">
                  <Select value={m.type} onValueChange={(v) => updateInf(i, 'type', v)}>
                    <SelectTrigger data-testid={`inf-type-${i}`}><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>{INFORMAL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                  <Textarea
                    placeholder="Findings (e.g. 15+ gestalts produced during play)"
                    value={m.findings}
                    onChange={(e) => updateInf(i, 'findings', e.target.value)}
                    rows={2}
                    data-testid={`inf-findings-${i}`}
                  />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeInf(i)} disabled={form.informal.length <= 1} data-testid={`inf-remove-${i}`}>
                    <Trash2 className="w-4 h-4 text-stone-400" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addInf} className="rounded-full" data-testid="inf-add-btn">
                <Plus className="w-4 h-4 mr-1" /> Add informal measure
              </Button>
            </div>
          </Section>

          {/* SECTION 3 — Quick clinical fields */}
          <Section number="3" title="Quick clinical fields" subtitle="Just a few words in any field — AI will expand each into the right report section.">
            <QuickField label="What you observed" placeholder="e.g. uses delayed echolalia from preferred shows, strong joint attention" field="observed" form={form} onChange={(v) => update('observed', v)} dictate={dictate} recording={recordingField === 'observed'} />
            <QuickField label="What they did well" placeholder="e.g. multimodal communication, regulated transitions with visual support" field="strengths" form={form} onChange={(v) => update('strengths', v)} dictate={dictate} recording={recordingField === 'strengths'} />
            <QuickField label="What was hard / functional impact" placeholder="e.g. dysregulated during loud transitions, limits classroom participation" field="challenges" form={form} onChange={(v) => update('challenges', v)} dictate={dictate} recording={recordingField === 'challenges'} />
            <QuickField label="Your clinical impression (optional)" placeholder="e.g. patterns consistent with NLA Stage 2 — emerging mitigation" field="clinical_impression" form={form} onChange={(v) => update('clinical_impression', v)} dictate={dictate} recording={recordingField === 'clinical_impression'} />
          </Section>

          {/* SECTION 4 — Writing style */}
          <Section number="4" title="Writing style (optional, one-time)" subtitle="Train PlayFirst Lite to write in your voice.">
            <WritingStylePanel onChange={() => {}} />
          </Section>

          {/* SECTION 5 — Output options */}
          <Section number="5" title="Output options">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Checkbox id="rec" checked={form.include_recommendations} onCheckedChange={(v) => update('include_recommendations', !!v)} data-testid="report-include-recs" />
                <Label htmlFor="rec" className="cursor-pointer">Include Recommendations</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="goals" checked={form.include_goals} onCheckedChange={(v) => update('include_goals', !!v)} data-testid="report-include-goals" />
                <Label htmlFor="goals" className="cursor-pointer">Include Goals</Label>
              </div>
              <div className="space-y-1.5">
                <Label>Tone</Label>
                <Select value={form.tone} onValueChange={(v) => update('tone', v)}>
                  <SelectTrigger data-testid="report-tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TONE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Audience</Label>
                <Select value={form.audience} onValueChange={(v) => update('audience', v)}>
                  <SelectTrigger data-testid="report-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{AUDIENCE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </Section>

          <div className="flex justify-end pt-2">
            <Button onClick={goToDeId} className="rounded-full" data-testid="report-next-btn">
              Next: Clean &amp; review
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <DeIdPanel
          text={combinedFreeText}
          onProceed={proceedAfterDeId}
          onCancel={() => setStep(1)}
        />
      )}

      {step === 3 && (
        <div className="flex flex-col items-center justify-center py-16 text-stone-600" data-testid="lite-generating">
          <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
          <p className="font-medium">Generating your report draft…</p>
          <p className="text-sm text-stone-500 mt-1">This usually takes 15–30 seconds.</p>
        </div>
      )}

      {step === 4 && output && (
        <OutputView
          output={output}
          toolLabel="Clinical Report"
          tool="report"
          usage={usage}
          onRegenerate={() => runGenerate(combinedFreeText)}
          onStartOver={() => { setOutput(''); setStep(1); }}
          onWaitlist={() => { setGateInfo({ used: 0, limit: 0 }); setGateOpen(true); }}
        />
      )}

      <RateLimitGate
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        used={gateInfo.used}
        limit={gateInfo.limit}
        onUnlocked={() => setGateOpen(false)}
      />
    </LiteShell>
  );
}

function Section({ number, title, subtitle, children }) {
  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-5 space-y-4">
      <div>
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-primary">Section {number}</span>
          <h2 className="font-medium text-primary-900">{title}</h2>
        </div>
        {subtitle && <p className="text-xs text-stone-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

function QuickField({ label, placeholder, field, form, onChange, dictate, recording }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        {voiceSupported() && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => dictate(field)}
            className={`h-6 px-2 rounded-full text-xs ${recording ? 'text-red-600 bg-red-50' : 'text-stone-500'}`}
            data-testid={`report-q-${field}-dictate`}
          >
            <Mic className={`w-3 h-3 mr-1 ${recording ? 'animate-pulse' : ''}`} />
            {recording ? 'Listening…' : 'Dictate'}
          </Button>
        )}
      </div>
      <Textarea
        placeholder={placeholder}
        value={form[field] || ''}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        data-testid={`report-q-${field}`}
      />
    </div>
  );
}
